import { useState, useEffect, useCallback, createContext, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { 
    fetchAuthConfig,
    exchangeToken,
    fetchUserProfile, 
    validateToken, 
    logoutUser 
} from '../../services/api';

// Auth keys — sessionStorage only (CON-3585: do not persist JWT in localStorage)
const AUTH_TOKEN_KEY = 'auth_token';
const AUTH_USER_KEY = 'auth_user';
const AUTH_EXPIRES_KEY = 'auth_expires';

// Create Auth Context
const AuthContext = createContext(null);

// MSAL instance (singleton)
let msalInstance = null;
let msalConfig = null;

// Default scopes for Microsoft Graph API
const DEFAULT_SCOPES = ['openid', 'profile', 'email', 'User.Read'];

/** Remove OAuth hash/query params from the URL after MSAL redirect (avoids history + code leakage). */
function clearAuthCodeFromUrl() {
    if (typeof window === 'undefined') return;
    const { pathname, hash, search } = window.location;
    const hasAuthFragment = hash && /(^|[&#])(code|error|state|session_state)=/i.test(hash);
    const hasAuthQuery = search && /(^|[?&])(code|error|state|session_state)=/i.test(search);
    if (hasAuthFragment || hasAuthQuery) {
        window.history.replaceState({}, document.title, pathname);
    }
}

/**
 * Initialize MSAL instance with config from backend
 */
async function initializeMsal() {
    if (msalInstance) return msalInstance;

    try {
        // Fetch config from backend
        const backendConfig = await fetchAuthConfig();
        // console.log('Received auth config from backend:', backendConfig);
        
        // Normalize backend response (handle both camelCase and snake_case)
        const config = {
            client_id: backendConfig.clientId || backendConfig.client_id,
            authority: backendConfig.authority,
            tenant_id: backendConfig.tenantId || backendConfig.tenant_id,
            redirect_uri: backendConfig.redirectUri || backendConfig.redirect_uri || window.location.origin,
            scopes: backendConfig.scopes || DEFAULT_SCOPES
        };
        
        // Validate required fields
        if (!config.client_id) {
            throw new Error('Missing clientId in auth config');
        }
        if (!config.authority) {
            throw new Error('Missing authority in auth config');
        }
        
        // console.log('Normalized auth config:', config);
        
        // Ensure scopes has default values
        config.scopes = config.scopes && config.scopes.length > 0 ? config.scopes : DEFAULT_SCOPES;
        msalConfig = config;

        const msalConfiguration = {
            auth: {
                clientId: config.client_id,
                authority: config.authority,
                redirectUri: config.redirect_uri,
                postLogoutRedirectUri: window.location.origin,
                navigateToLoginRequestUrl: false,
            },
            cache: {
                cacheLocation: 'sessionStorage', // CON-3585: avoid persisting auth cache in localStorage
                storeAuthStateInCookie: true, // Required for redirect flow
            },
            system: {
                loggerOptions: {
                    logLevel: 0, // Error level only in production
                }
            }
        };

        // console.log('Initializing MSAL with configuration:', msalConfiguration);

        msalInstance = new PublicClientApplication(msalConfiguration);
        await msalInstance.initialize();
        
        // console.log('MSAL initialized successfully');
        
        return msalInstance;
    } catch (error) {
        console.error('Failed to initialize MSAL:', error);
        throw error;
    }
}

/**
 * Auth Provider Component
 * Wraps the app and provides authentication state and methods
 */
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [error, setError] = useState(null);
    const [msalReady, setMsalReady] = useState(false);
    const initializingRef = useRef(false);

    // Clear auth data from state and sessionStorage (also strip legacy localStorage keys)
    const clearAuthData = useCallback(() => {
        sessionStorage.removeItem(AUTH_TOKEN_KEY);
        sessionStorage.removeItem(AUTH_USER_KEY);
        sessionStorage.removeItem(AUTH_EXPIRES_KEY);
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(AUTH_USER_KEY);
        localStorage.removeItem(AUTH_EXPIRES_KEY);
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
        setError(null);
    }, []);

    // Handle MSAL response and exchange for backend token
    const handleMsalResponse = useCallback(async (response) => {
        try {
            setIsLoading(true);

            // Exchange Microsoft token for backend JWT
            const backendResponse = await exchangeToken(response.accessToken);

            if (backendResponse.access_token) {
                // Calculate expiration time
                const expiresAt = Date.now() + (backendResponse.expires_in * 1000);

                // Store auth data in sessionStorage (CON-3585)
                sessionStorage.setItem(AUTH_TOKEN_KEY, backendResponse.access_token);
                sessionStorage.setItem(AUTH_USER_KEY, JSON.stringify(backendResponse.user));
                sessionStorage.setItem(AUTH_EXPIRES_KEY, expiresAt.toString());
                localStorage.removeItem(AUTH_TOKEN_KEY);
                localStorage.removeItem(AUTH_USER_KEY);
                localStorage.removeItem(AUTH_EXPIRES_KEY);

                setToken(backendResponse.access_token);
                setUser(backendResponse.user);
                setIsAuthenticated(true);
                setError(null);

                return { success: true, user: backendResponse.user };
            }
            return { success: false, error: 'No access token received' };
        } catch (err) {
            console.error('Token exchange error:', err);
            setError(err.message || 'Failed to complete authentication');
            clearAuthData();
            return { success: false, error: err.message };
        } finally {
            setIsLoading(false);
        }
    }, [clearAuthData]);

    // Attempt silent login using MSAL
    const attemptSilentLogin = useCallback(async () => {
        if (!msalInstance || !msalConfig) {
            clearAuthData();
            return false;
        }

        try {
            const accounts = msalInstance.getAllAccounts();
            
            if (accounts && accounts.length > 0) {
                // Ensure scopes are always an array with at least default values
                const scopes = msalConfig.scopes && msalConfig.scopes.length > 0 
                    ? msalConfig.scopes 
                    : DEFAULT_SCOPES;

                const silentRequest = {
                    scopes: scopes,
                    account: accounts[0],
                };

                const response = await msalInstance.acquireTokenSilent(silentRequest);
                if (response && response.accessToken) {
                    await handleMsalResponse(response);
                    return true;
                }
            }
            clearAuthData();
            return false;
        } catch (err) {
            if (err instanceof InteractionRequiredAuthError) {
                // console.log('Silent login failed, user interaction required');
            } else {
                console.error('Silent login error:', err);
            }
            clearAuthData();
            return false;
        }
    }, [clearAuthData, handleMsalResponse]);

    // Check for existing authentication
    const checkExistingAuth = useCallback(async () => {
        try {
            const storedToken = sessionStorage.getItem(AUTH_TOKEN_KEY);
            const storedUser = sessionStorage.getItem(AUTH_USER_KEY);
            const storedExpires = sessionStorage.getItem(AUTH_EXPIRES_KEY);

            if (!storedToken) {
                clearAuthData();
                return;
            }

            // Check if token is expired
            if (storedExpires && Date.now() > parseInt(storedExpires)) {
                // console.log('Token expired, attempting silent refresh...');
                await attemptSilentLogin();
                return;
            }

            // Validate token with backend
            const validation = await validateToken(storedToken);

            if (validation.valid) {
                setToken(storedToken);
                setUser(storedUser ? JSON.parse(storedUser) : validation.user);
                setIsAuthenticated(true);
            } else {
                // Token invalid, try silent refresh
                const refreshed = await attemptSilentLogin();
                if (!refreshed) {
                    clearAuthData();
                }
            }
        } catch (err) {
            console.error('Error checking existing auth:', err);
            clearAuthData();
        }
    }, [clearAuthData, attemptSilentLogin]);

    // Initialize MSAL and check for existing auth
    useEffect(() => {
        const initialize = async () => {
            if (initializingRef.current) return;
            initializingRef.current = true;

            try {
                // Initialize MSAL
                await initializeMsal();
                setMsalReady(true);

                // Handle redirect callback if present (user returning from Microsoft login)
                const response = await msalInstance.handleRedirectPromise();
                
                if (response && response.accessToken) {
                    // User just logged in via redirect
                    // console.log('Handling redirect response...');
                    await handleMsalResponse(response);
                    clearAuthCodeFromUrl();
                } else {
                    // Check for existing token in sessionStorage
                    await checkExistingAuth();
                }
            } catch (err) {
                console.error('Auth initialization error:', err);
                // Provide user-friendly error message
                if (err.message.includes('Network error') || err.message.includes('fetch')) {
                    setError('Unable to connect to authentication service. Please check your connection and try again.');
                } else if (err.message.includes('Missing')) {
                    setError('Authentication configuration error. Please contact support.');
                } else {
                    setError('Failed to initialize authentication. Please refresh the page or contact support.');
                }
                clearAuthData();
                setMsalReady(false); // Mark MSAL as not ready on error
            } finally {
                setIsLoading(false);
            }
        };

        initialize();
    }, [handleMsalResponse, checkExistingAuth, clearAuthData]);

    // Initiate Microsoft SSO login via redirect (same tab)
    const login = useCallback(async () => {
        if (!msalInstance || !msalConfig) {
            setError('Authentication not initialized. Please refresh the page.');
            throw new Error('Authentication not initialized');
        }

        try {
            setIsLoading(true);
            setError(null);

            // Ensure scopes are always an array with at least default values
            const scopes = msalConfig.scopes && msalConfig.scopes.length > 0 
                ? msalConfig.scopes 
                : DEFAULT_SCOPES;

            const loginRequest = {
                scopes: scopes,
            };

            // console.log('Initiating login redirect with scopes:', scopes);

            // Use redirect flow (same tab, no popup)
            await msalInstance.loginRedirect(loginRequest);
            // Page will redirect to Microsoft, then back to app
            // The handleRedirectPromise in useEffect will handle the response
        } catch (err) {
            console.error('Login error:', err);
            setError(err.message || 'Failed to login');
            setIsLoading(false);
            throw err;
        }
    }, []);

    // Logout user
    const logout = useCallback(async () => {
        try {
            setIsLoading(true);

            // Call backend logout
            if (token) {
                try {
                    await logoutUser(token);
                } catch (err) {
                    console.error('Backend logout error:', err);
                }
            }

            // Clear local auth data first
            clearAuthData();

            // Logout from MSAL (clears Microsoft session) - use redirect
            if (msalInstance) {
                const accounts = msalInstance.getAllAccounts();
                if (accounts && accounts.length > 0) {
                    // Use logout redirect (same tab)
                    await msalInstance.logoutRedirect({
                        account: accounts[0],
                        postLogoutRedirectUri: window.location.origin,
                    });
                } else {
                    setIsLoading(false);
                }
            } else {
                setIsLoading(false);
            }
        } catch (err) {
            console.error('Logout error:', err);
            clearAuthData();
            setIsLoading(false);
        }
    }, [token, clearAuthData]);

    // Refresh user profile from backend
    const refreshUser = useCallback(async () => {
        if (!token) return null;

        try {
            const userProfile = await fetchUserProfile(token);
            setUser(userProfile);
            sessionStorage.setItem(AUTH_USER_KEY, JSON.stringify(userProfile));
            return userProfile;
        } catch (err) {
            console.error('Error refreshing user:', err);
            return null;
        }
    }, [token]);

    // Check if token is valid
    const checkAuth = useCallback(async () => {
        if (!token) return false;

        try {
            const validation = await validateToken(token);
            if (!validation.valid) {
                // Try silent refresh before clearing
                const refreshed = await attemptSilentLogin();
                return refreshed;
            }
            return true;
        } catch (err) {
            clearAuthData();
            return false;
        }
    }, [token, clearAuthData, attemptSilentLogin]);

    // Get a valid token (refreshing if needed)
    const getToken = useCallback(async () => {
        if (!token) return null;

        const storedExpires = sessionStorage.getItem(AUTH_EXPIRES_KEY);
        
        // If token expires in less than 5 minutes, try to refresh
        if (storedExpires && Date.now() > parseInt(storedExpires) - 300000) {
            await attemptSilentLogin();
            return sessionStorage.getItem(AUTH_TOKEN_KEY);
        }

        return token;
    }, [token, attemptSilentLogin]);

    // Retry initialization (useful when config fetch fails)
    const retryInitialization = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        initializingRef.current = false; // Reset initialization flag
        
        try {
            // Re-initialize MSAL
            msalInstance = null; // Reset singleton
            await initializeMsal();
            setMsalReady(true);
            
            // Check for existing auth
            await checkExistingAuth();
        } catch (err) {
            console.error('Retry initialization error:', err);
            if (err.message.includes('Network error') || err.message.includes('fetch')) {
                setError('Unable to connect to authentication service. Please check your connection and try again.');
            } else if (err.message.includes('Missing')) {
                setError('Authentication configuration error. Please contact support.');
            } else {
                setError('Failed to initialize authentication. Please refresh the page or contact support.');
            }
            clearAuthData();
            setMsalReady(false);
        } finally {
            setIsLoading(false);
        }
    }, [clearAuthData, checkExistingAuth]);

    const value = {
        user,
        token,
        isLoading,
        isAuthenticated,
        error,
        msalReady,
        login,
        logout,
        refreshUser,
        checkAuth,
        getToken,
        clearError: () => setError(null),
        retryInitialization
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

/**
 * Custom hook to use auth context
 * @returns {object} Auth context value
 */
export function useAuth() {
    const context = useContext(AuthContext);
    
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    
    return context;
}

/**
 * Higher-order component for protected routes
 * Redirects to login if user is not authenticated
 */
export function ProtectedRoute({ children }) {
    const { isAuthenticated, isLoading } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!isLoading && !isAuthenticated) {
            navigate('/?login=required');
        }
    }, [isAuthenticated, isLoading, navigate]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
            </div>
        );
    }

    return isAuthenticated ? children : null;
}

export default useAuth;
